import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TabStrip } from '@shared-ui/components';
import { SimulationsGallery, SimulationChapters } from '@shared-ui/simulations';
import ChemistryBench from '@shared-ui/chemistry-lab/ChemistryBench';
import { MechanicsBench } from '@shared-ui/mechanics-bench';
import { OpticsBench } from '@shared-ui/optics-bench';
import { adminCatalogueSource, adminSimulationsSource, type CatalogueRow } from '../../api/simulationsApi';
import { adminChemistryLabApi } from '../../api/chemistryLabApi';
import { authApi } from '../../api/auth';
import { canCurateChemistry } from '../../config/adminRoles';
import ChemistryLabReview from '../ChemistryLabReview';
import './Simulations.css';

/**
 * Everything interactive, in one place: the catalogue (simulations and guided
 * experiments together, as zitmus lists them), its chapters, the two benches,
 * and the reaction review that decides what the chemistry bench may say.
 *
 * Reactions and the experiment rows are for chemistry curators only; the rest
 * of the page is open to tenant admins too.
 */
type Tab = 'simulations' | 'chapters' | 'chemistry' | 'quantitative-chemistry' | 'reactions' | 'mechanics' | 'optics';

const TABS: { key: Tab; label: string; curatorsOnly?: boolean }[] = [
  { key: 'simulations', label: 'Simulations' },
  { key: 'chapters', label: 'Chapters' },
  { key: 'chemistry', label: 'Chemistry Lab' },
  { key: 'quantitative-chemistry', label: 'Chemistry Lab — Quantitative' },
  { key: 'reactions', label: 'Reactions', curatorsOnly: true },
  { key: 'mechanics', label: 'Mechanics Lab' },
  { key: 'optics', label: 'Optics Lab' },
];

const experimentHref = (row: CatalogueRow): string | undefined =>
  row.kind === 'experiment' ? `/simulations/experiments/${row.id}` : undefined;

export function Simulations() {
  // In the URL, like the teacher page: a tab is worth reloading into and worth
  // sending to somebody. The galleries keep their own filter keys there too.
  const [params, setParams] = useSearchParams();
  const curator = canCurateChemistry(authApi.getUser());
  const tabs = useMemo(() => TABS.filter(t => curator || !t.curatorsOnly), [curator]);
  const source = useMemo(() => adminCatalogueSource(curator), [curator]);
  const tab = (tabs.find(t => t.key === params.get('tab'))?.key ?? 'simulations') as Tab;
  const setTab = (next: Tab) => setParams(prev => {
    const p = new URLSearchParams(prev);
    if (next === 'simulations') p.delete('tab'); else p.set('tab', next);
    return p;
  }, { replace: true });

  const isBench = tab === 'chemistry' || tab === 'quantitative-chemistry' || tab === 'mechanics' || tab === 'optics';

  return (
    <div className="split-layout admin-sims">
      <div className="admin-sims-head">
        <TabStrip
          tabs={tabs}
          active={tab}
          onChange={setTab}
          ariaLabel="Simulations views"
        />
      </div>

      <div className={`admin-sims-body ${isBench ? 'is-bench' : ''}`}>
        {/* `heading={false}`: the tabs say where you are. */}
        {tab === 'simulations' && (
          <SimulationsGallery
            source={source}
            hrefFor={experimentHref}
            curation
            heading={false}
            columnToggleKey="admin-simulations"
          />
        )}
        {tab === 'chapters' && <SimulationChapters source={adminSimulationsSource} />}
        {/* Each bench is mounted only while its tab shows: a chemistry session is
            opened by the first mix, and an unmounted bench is not quietly holding
            one open behind another tab. */}
        {tab === 'chemistry' && <ChemistryBench api={adminChemistryLabApi} />}
        {tab === 'quantitative-chemistry' && <ChemistryBench api={adminChemistryLabApi} quantitative />}
        {tab === 'reactions' && <ChemistryLabReview />}
        {tab === 'mechanics' && <MechanicsBench />}
        {tab === 'optics' && <OpticsBench />}
      </div>
    </div>
  );
}
